import { AxiosRequestConfig } from 'axios';
import AxiosInstance from 'src/shared/services/axios/interceptor';
import { PagedResult } from 'src/shared/models/pagedResult';
import { HelpPage } from '../models/helpPage';
import { HelpPageVersion } from '../models/helpPageVersion';
import { HelpPageSearchFilter } from '../models/helpPageSearchFilter';
import { HelpPageCreateRequest, HelpPageEditRequest } from '../models/helpPageEditRequest';
import { HelpPageListItem } from '../models/helpPageListItem';

const controller = 'helppages';

// URLSearchParams kodar söktexten och skickar categoryIds som upprepad parameter (categoryIds=7&categoryIds=9), som
// API:t binder. Värden läggs bara till när de finns, så att status 0 (Utkast) följer med men inget "undefined" skickas.
export const getAllHelpPages = (searchFilter: HelpPageSearchFilter, config?: AxiosRequestConfig) => {
  const { pageIndex, pageSize, sortCol, sortDir, filter, categoryId, categoryIds, moduleId, status, includeArchived } = searchFilter;
  const params = new URLSearchParams({ pageIndex: String(pageIndex), pageSize: String(pageSize) });
  if (sortCol) params.append('sortCol', sortCol);
  if (sortDir) params.append('sortDir', sortDir);
  if (filter) params.append('filter', filter);
  if (categoryIds?.length) {
    categoryIds.forEach((id) => params.append('categoryIds', String(id)));
  } else if (categoryId !== undefined) {
    params.append('categoryId', String(categoryId));
  }
  if (moduleId !== undefined) params.append('moduleId', String(moduleId));
  if (status !== undefined) params.append('status', String(status));
  if (includeArchived) params.append('includeArchived', 'true');

  return AxiosInstance.get<PagedResult<HelpPageListItem>>(`${controller}?${params.toString()}`, config);
};

export const getHelpPageById = (id: string) => {
  return AxiosInstance.get<HelpPage>(`${controller}/${id}`);
};

// Nyast först. config: med skipErrorToast visar anroparen inget eget fel för versionerna.
export const getHelpPageVersions = (id: string, config?: AxiosRequestConfig) => {
  return AxiosInstance.get<HelpPageVersion[]>(`${controller}/${id}/versions`, config);
};

// Skapar sidan med version 1 som utkast. Vem som sparar tar API:t från inloggningen.
export const createHelpPage = (data: HelpPageCreateRequest) => {
  return AxiosInstance.post<HelpPage>(controller, data);
};

// Varje sparning blir en ny version.
export const updateHelpPage = (id: string, data: HelpPageEditRequest) => {
  return AxiosInstance.put<HelpPageVersion>(`${controller}/${id}`, data);
};

// skipErrorToast: felet hanteras med egen toast i redigeringsvyn (utkastet är redan sparat).
export const publishHelpPageVersion = (id: string, versionId: string) => {
  return AxiosInstance.post<HelpPage>(`${controller}/${id}/publish`, { versionId }, { skipErrorToast: true });
};

// Kopierar versionen till en ny version som publiceras direkt.
export const restoreHelpPageVersion = (id: string, versionId: string) => {
  return AxiosInstance.post<HelpPageVersion>(`${controller}/${id}/versions/${versionId}/restore`);
};

// Mjuk borttagning: sidan får status Arkiverad och finns kvar med sina versioner.
export const archiveHelpPage = (id: string) => {
  return AxiosInstance.delete(`${controller}/${id}`);
};

// Kategorins egna artiklar (inte underkategoriernas) som inte är arkiverade, i den ordning hjälpcentret visar dem.
// Alla på en sida, eftersom ordningen bara kan sparas för hela kategorin.
export const getHelpPagesInCategoryOrder = (categoryId: number) => {
  return getAllHelpPages({ pageIndex: 0, pageSize: 1000, sortCol: 'sortOrder', sortDir: 'asc', categoryId });
};

// Antalet egna artiklar som inte är arkiverade (totalItems). Bara en rad hämtas. skipErrorToast: anroparen döljer
// bara det som behöver antalet om det misslyckas.
export const countHelpPagesInCategory = (categoryId: number) => {
  return getAllHelpPages({ pageIndex: 0, pageSize: 1, sortCol: 'sortOrder', sortDir: 'asc', categoryId }, { skipErrorToast: true });
};

// Hela kategorins artiklar, den översta först. Har artiklarna i kategorin ändrats sedan listan hämtades svarar API:t 400
// med ett meddelande, som interceptorn visar.
export const saveHelpPageOrder = (categoryId: number, pageIds: string[]) => {
  return AxiosInstance.put(`${controller}/order`, { categoryId, pageIds });
};
